'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetUserActivityRow, type ItemsRenderProps, useGetUserActivityField, useGetUserActivityInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { Ref } from 'react';
import { InfiniteList } from '@/components/ui/infinite-list';
import { RetryButton } from '@/components/ui/retry-button';
import { ACTIVITY_FEED_FIELDS } from '@/lib/sdk/projections';
import { isActivityAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
import { ActivityRow, ActivityRowSkeleton } from './portfolio-activity-feed-row';
interface PortfolioActivityFeedProps {
  userId: string;
  isOwn: boolean;
  /**
   * Every wallet pubkey the viewed user owns. The SDK fans out one wire
   * subscription per element so chain events from EVERY wallet patch the
   * cache (a single-wallet subscription silently misses the rest). The
   * REST URL identity (`userId`) is unchanged.
   */
  walletPubkeys: readonly string[];
  /**
   * User ids to subscribe to for gift realtime — `[userId]` for the owner,
   * `[]` for other viewers. Gift rows are private to the owner, so a
   * non-owner passes an empty array and the SDK creates no gift
   * subscription (the REST feed already excludes gift rows for non-owners).
   */
  giftUserIds: readonly string[];
  /** Null keeps the ON-state feed; a pubkey fails every other row closed. */
  attributionWalletAddress: string | null;
}

/**
 * Activity history panel feed — driven off the unified v2 `useGetUserActivity`
 * hook. Surfaces every chain-derived event the user participated in
 * (trades, claims, backs/unbacks, boosts) in one timestamp-sorted stream,
 * plus gift sends + receives for the profile owner only. Realtime patching
 * on the market-user subjects
 * (`morfi.market.*.user.${walletPubkey}.{trade,backing,boost,claim}.v0` × every
 * wallet the user owns, + `morfi.user.${giftUserId}.gift.v0` × `giftUserIds`)
 * is wired automatically via the SDK manifest. `walletPubkeys` is the IDENTITY
 * ARRAY the indexer publishes chain events on; `giftUserIds` is `[userId]`
 * for the owner and `[]` for other viewers so gift rows never leak via
 * realtime (`userId` would not match any wire subject).
 */
export function PortfolioActivityFeed(t0) {
  const $ = _c(56);
  const {
    userId,
    isOwn,
    walletPubkeys,
    giftUserIds,
    attributionWalletAddress
  } = t0;
  const t = useTranslations("profile.stats");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...ACTIVITY_FEED_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== giftUserIds || $[2] !== userId || $[3] !== walletPubkeys) {
    t2 = {
      userId,
      walletPubkeys,
      giftUserIds,
      fields: t1
    };
    $[1] = giftUserIds;
    $[2] = userId;
    $[3] = walletPubkeys;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useGetUserActivityInfinite(t2);
  const visibleRows = filterActivityRows(query.rows, attributionWalletAddress);
  const hasVisibleRows = attributionWalletAddress === null ? query.ids.length > 0 : visibleRows.length > 0;
  if (query.isPending) {
    let t3;
    if ($[5] !== t) {
      t3 = t("activity_loading");
      $[5] = t;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t3) {
      t4 = <output className="sr-only">{t3}</output>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <ul className="m-0 flex list-none flex-col gap-y-1 p-0" data-testid="activity-feed-skeleton"><ActivityRowSkeleton /><ActivityRowSkeleton /><ActivityRowSkeleton /></ul>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4) {
      t6 = <>{t4}{t5}</>;
      $[10] = t4;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (query.isError && !hasVisibleRows) {
    let t3;
    if ($[12] !== t) {
      t3 = t("activity_error_title");
      $[12] = t;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t3) {
      t4 = <h3 className="text-heading-md">{t3}</h3>;
      $[14] = t3;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== t) {
      t5 = t("activity_error_body");
      $[16] = t;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] !== t5) {
      t6 = <p className="max-w-sm text-body text-foreground-secondary">{t5}</p>;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== query) {
      t7 = () => {
        query.refetch();
      };
      $[20] = query;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== t) {
      t8 = t("activity_retry");
      $[22] = t;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== t7 || $[25] !== t8) {
      t9 = <RetryButton onRetry={t7}>{t8}</RetryButton>;
      $[24] = t7;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t4 || $[28] !== t6 || $[29] !== t9) {
      t10 = <div className="flex flex-col items-center justify-center gap-3 py-12 text-center" data-testid="activity-feed-error">{t4}{t6}{t9}</div>;
      $[27] = t4;
      $[28] = t6;
      $[29] = t9;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    return t10;
  }
  if (!hasVisibleRows && query.hasNextPage !== true) {
    let t3;
    if ($[31] !== t) {
      t3 = t("activity_empty");
      $[31] = t;
      $[32] = t3;
    } else {
      t3 = $[32];
    }
    let t4;
    if ($[33] !== t3) {
      t4 = <p className="py-6 text-center text-body text-foreground-secondary">{t3}</p>;
      $[33] = t3;
      $[34] = t4;
    } else {
      t4 = $[34];
    }
    return t4;
  }
  let t3;
  if ($[35] !== query.isFetchingNextPage || $[36] !== t) {
    t3 = query.isFetchingNextPage ? <output className="sr-only">{t("activity_loading_older")}</output> : null;
    $[35] = query.isFetchingNextPage;
    $[36] = t;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  const t4 = query.hasNextPage === true && query.nextPageError === null;
  let t5;
  if ($[38] !== attributionWalletAddress || $[39] !== isOwn) {
    t5 = t6 => {
      const {
        ref,
        id
      } = t6;
      return <AttributedActivityRow ref={ref} rowId={id} isOwn={isOwn} attributionWalletAddress={attributionWalletAddress} />;
    };
    $[38] = attributionWalletAddress;
    $[39] = isOwn;
    $[40] = t5;
  } else {
    t5 = $[40];
  }
  let t6;
  if ($[41] !== query.Items || $[42] !== t5) {
    t6 = <ul className="m-0 flex list-none flex-col gap-y-1 p-0"><query.Items>{t5}</query.Items></ul>;
    $[41] = query.Items;
    $[42] = t5;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  let t7;
  if ($[44] !== query.fetchNextPage || $[45] !== query.isFetchingNextPage || $[46] !== t4 || $[47] !== t6) {
    t7 = <InfiniteList hasNextPage={t4} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={query.fetchNextPage}>{t6}</InfiniteList>;
    $[44] = query.fetchNextPage;
    $[45] = query.isFetchingNextPage;
    $[46] = t4;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== query || $[50] !== t) {
    t8 = query.nextPageError !== null ? <div className="flex items-center justify-center gap-3 py-3 text-center" data-testid="activity-feed-pagination-error" aria-live="polite"><p className="text-caption text-foreground-secondary">{t("activity_older_error")}</p><RetryButton variant="tertiary" size="sm" onRetry={() => {
        query.retryNextPage().catch(_temp);
      }}>{t("activity_retry")}</RetryButton></div> : null;
    $[49] = query;
    $[50] = t;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[52] !== t3 || $[53] !== t7 || $[54] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[52] = t3;
    $[53] = t7;
    $[54] = t8;
    $[55] = t9;
  } else {
    t9 = $[55];
  }
  return t9;
}
function _temp() {}
function filterActivityRows(rows: readonly GetUserActivityRow[], attributionWalletAddress: string | null): readonly GetUserActivityRow[] {
  if (attributionWalletAddress === null) return rows;
  return rows.filter(row => isActivityAttributedToWallet(row, attributionWalletAddress));
}
function AttributedActivityRow(t0) {
  const $ = _c(6);
  const {
    ref,
    rowId,
    isOwn,
    attributionWalletAddress
  } = t0;
  const trader = useGetUserActivityField(rowId, "trader") as string | undefined ?? null;
  const participationId = useGetUserActivityField(rowId, "participationId") as string | undefined ?? null;
  if (attributionWalletAddress !== null && !isActivityAttributedToWallet({
    trader,
    participationId
  }, attributionWalletAddress)) {
    let t1;
    if ($[0] !== ref) {
      t1 = <li ref={ref} hidden={true} aria-hidden="true" />;
      $[0] = ref;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== isOwn || $[3] !== ref || $[4] !== rowId) {
    t1 = <ActivityRow ref={ref} rowId={rowId} isOwn={isOwn} />;
    $[2] = isOwn;
    $[3] = ref;
    $[4] = rowId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}